import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { MfdContext } from '@/contexts'
import {get, isApp, isIOS, isMobile, omniStorage, setNestedProp, storage} from '@/helpers'
import { LAST_LOCATION, LAST_SHARD_NAME, TEST_EMAILS } from '@/config/settings'
import * as Schema from 'yup'
import deepEqual from 'fast-deep-equal'
import Loading from '@/components/Loading'
import { ClientWebRTC, CustomEvents, LogTypes, PeerEvents } from '@gameglass/webrtc'
import { ErrorEnum } from '@/utils/announcements'
import { ACTION_TYPES } from '@gameglass/common'
import { penpal } from '@/components/ShardIframe'
import { useUser } from '@clerk/clerk-react'
import { Button } from '@/components/common/Button'
import { useLocation, useNavigate, useRouter } from '@tanstack/react-router'
import { IS_DEV, WEBRTC_HTTP_URL, WEBRTC_TOKEN_URL, WEBRTC_WEBSOCKET_URL } from '@/constants'
import NoSleep from 'nosleep.js'
import {useAnnouncementsContext} from "@/contexts/AnnouncementsContext";

const noSleep = new NoSleep()

const CONNECTED_TIMEOUT = 30000

const ConnectedStates = {
  CONNECTED: true,
  DISCONNECTED: false,
  CONNECTING: null
}

const mfdOmniStorage = omniStorage.prefixed('MFD_')

const soundDirectory = 'sound/'

const keyboardLayouts: Record<string, Record<string, string>> = {
  qwertz: {
    y: 'z',
    z: 'y'
  },
  azerty: {
    q: 'a',
    w: 'z',
    a: 'q',
    z: 'w'
  }
}

const convertKey = (keyboardLayout: string, key: string): string => {
  return get(keyboardLayouts, `${keyboardLayout}.${key}`) || key
}

const defaultSettings: Record<string, any> = {
  volume: 100,
  altSounds: {},
  keyboardLayout: ''
}

const getDefaultsAndStorageSettings = () => {
  return {
    ...defaultSettings,
    ...mfdOmniStorage.get('SETTINGS')
  }
}

const noUnknownArgs: [boolean] = [true]

const settingsSchema = Schema
  .object()
  .noUnknown(...noUnknownArgs)
  .shape({
    altSounds: Schema
      .object()
      .default(defaultSettings.altSounds),
    volume: Schema
      .number()
      .default(defaultSettings.volume),
    selectedGame: Schema
      .string()
      .default(defaultSettings.selectedGame),
  })

const validateSettings = (settings: any, user: any, retries = 0): any => {
  if (retries >= 50) {
    console.error('Possible validation loop. Using previous or default settings.')
    return getDefaultsAndStorageSettings()
  }

  try {
    return settingsSchema.validateSync(settings)
  } catch (error: any) {
    console.error('=========VALIDATION ERROR=========')
    console.error(error)

    setNestedProp(
      settings,
      error.path,
      get(defaultSettings, error.path)
    )

    return validateSettings(settings, user, retries + 1)
  }
}

class Queue {
  _processing = false
  _queue: (() => Promise<void>)[] = []

  _process = async () => {
    this._processing = true

    const callback = this._queue.shift()

    if (typeof callback === 'function') {
      await callback()
    }

    if (this._queue.length > 0) {
      this._process()
    } else {
      this._processing = false
    }
  }

  push = (callback: () => Promise<void>) => {
    this._queue.push(callback)

    if (!this._processing) {
      this._process()
    }
  }
}

export let webrtc: any = null

const allowedFields = ['type', 'payload']

const MFD = (() => {
  let mfdInstance: any = null

  let peerId: any = null

  const setPeerID = () => {
    if (peerId) {
      peerId = webrtc.peerId || Math.random()
    }

    return () => {
      const fingerprint = localStorage.getItem('fingerprint')
      webrtc.peerId = peerId && fingerprint ? `${peerId}-${fingerprint}` : webrtc.peerId
    }
  }

  webrtc = new ClientWebRTC({
    debug: IS_DEV,
    httpUrl: WEBRTC_HTTP_URL,
    webSocketUrl: WEBRTC_WEBSOCKET_URL,
    tokenUrl: WEBRTC_TOKEN_URL
  })

  setPeerID()

  return class MFD {
    static current: Record<string, any> = {
      mfd: null,
      settings: null,
      connected: null
    }

    queue = new Queue()
    _settings: any = null
    connected: any = null
    connectedCallback: any
    settingsChangedCallback: any
    user: any
    webrtc: any
    hasEverReceivedPrefs = false
    resolvers: Record<string, any[]> = { prefs: [] }

    constructor(options: any) {
      if (!mfdInstance) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        mfdInstance = this
      }
      const user = options.user
      this.connectedCallback = options.connectedCallback
      this.settingsChangedCallback = options.settingsChangedCallback

      mfdInstance.user = user

      if (!mfdInstance.webrtc) {
        mfdInstance.webrtc = this.webrtc = webrtc

        let timeout: any

        const onConnect = () => {
          clearTimeout(timeout)
          this.setConnected(ConnectedStates.CONNECTED)
          this.update()
        }

        const onError = (error: any) => {
          console.warn('WebRTC error', error)
          switch (this.connected) {
          case ConnectedStates.CONNECTING:
            clearTimeout(timeout)
            this.setConnected(ConnectedStates.DISCONNECTED)
            break
          case ConnectedStates.CONNECTED:
            if (!webrtc.peer?.connected) {
              this.setConnected(ConnectedStates.DISCONNECTED)
            }
          }
        }

        const onData = async ({ data }: any) => {
          this.setConnected(ConnectedStates.CONNECTED)

          const { type, payload } = data

          if ([
            ACTION_TYPES.getPrefs,
            ACTION_TYPES.setPrefs
          ].includes(type)) {
            if (type === ACTION_TYPES.getPrefs) {
              this.hasEverReceivedPrefs = true

              if (!deepEqual(payload.prefs, this.settings)) {
                this.settings = payload.prefs
              }
            }

            this.resolvers.prefs.forEach((resolve: any) => resolve(this.settings))
            this.resolvers.prefs = []
          }

          if (ACTION_TYPES.forgeUpdateSignal === type) {
            if (location.pathname.split('/').pop() === `${payload.id}`) {
              ;(await penpal!.promise as any).reload()
            }
          }
        }

        const onPeersChange = () => {
          const connected = !!webrtc.peer?.connected || ConnectedStates.CONNECTING
          this.setConnected(connected)

          if (connected) {
            clearTimeout(timeout)
          } else if (!timeout) {
            timeout = setTimeout(() => {
              this.setConnected(ConnectedStates.DISCONNECTED)
            }, CONNECTED_TIMEOUT)
          }
        }

        webrtc.on(PeerEvents.data, onData)
        webrtc.on(CustomEvents.peersChange, onPeersChange)
        webrtc.on(PeerEvents.error, onError)
        webrtc.on(PeerEvents.connect, onConnect)
        webrtc.on(CustomEvents.debugger, ({ data }: any) => {
          if (data?.title === LogTypes.signalReceived && data?.args?.[0]?.event === 'CANDIDATE') {
            this.setConnected(ConnectedStates.CONNECTING)
          }
        })
      }

      if (user) {
        if (!webrtc.token) {
          webrtc.token = user.publicMetadata.authToken
        }
        if (!webrtc.peer) {
          webrtc.start()
        }
      } else {
        webrtc.stop()
      }

      setPeerID()

      if (!mfdInstance.settings) {
        mfdInstance.settings = getDefaultsAndStorageSettings()
      }

      MFD.current.mfd = mfdInstance

      return mfdInstance
    }

    get settings() {
      return this._settings
    }

    set settings(settings: any) {
      const validatedSettings = validateSettings({
        ...getDefaultsAndStorageSettings(),
        ...this.settings,
        ...settings
      }, this.user)

      this._settings = validatedSettings
      MFD.current.settings = validatedSettings
      mfdOmniStorage.set('SETTINGS', validatedSettings)
      if (this.settingsChangedCallback) {
        this.settingsChangedCallback(this.settings)
      }
    }

    get preferences() {
      return this.settings
    }

    set preferences(settings: any) {
      this.settings = settings
    }

    setConnected = (connected: any) => {
      this.connected = connected
      MFD.current.connected = connected
      if (this.connectedCallback) {
        this.connectedCallback(this.connected)
      }
    }

    action = (_unused: any, active: any, mfdArray: any[], value?: any) => {
      if (active && mfdArray?.length > 0) {
        const keyboardLayout = this.settings.keyboardLayout
        for (let i = 0; i < mfdArray.length; i++) {
          const key = convertKey(keyboardLayout, mfdArray[i].key)

          console.warn(
            `Key: ${mfdArray[i].key || mfdArray[i].button}(${key}), modifiers: [${(mfdArray[i].modifierArray || []).join(', ')}], type: ${mfdArray[i].type}`
          )
          switch (mfdArray[i].type) {
          case 'mousePress':
            this.mouseClick(mfdArray[i].button).playMP3(mfdArray[i].sound)
            break
          case 'mouseOn':
          case 'mouseButtonDown':
            this.mouseOn(mfdArray[i].button).playMP3(mfdArray[i].sound)
            break
          case 'mouseOff':
          case 'mouseButtonUp':
            this.mouseOff(mfdArray[i].button).playMP3(mfdArray[i].sound)
            break
          case 'mouseWheel':
            this.mouseWheel(value || mfdArray[i].clicks).playMP3(mfdArray[i].sound)
            break
          case 'tap':
            this.keyTap(key, mfdArray[i].modifierArray, mfdArray[i].sound)
            break
          case 'dtap':
            this.keyTap(key, mfdArray[i].modifierArray, mfdArray[i].sound).keyTap(key, mfdArray[i].modifierArray)
            break
          case 'on':
            this.keyOn(key, mfdArray[i].modifierArray).playMP3(mfdArray[i].sound)
            break
          case 'onWait':
            this.keyOn(key, mfdArray[i].modifierArray)
              .playMP3(mfdArray[i].sound)
              .wait(mfdArray[i].wait.time)
              .keyOff(mfdArray[i].wait.keyOff, mfdArray[i].modifierArray)
            break
          case 'off':
            this.keyOff(key, mfdArray[i].modifierArray).playMP3(mfdArray[i].sound)
            break
          case 'console': {
            const string = key
              .split('')
              .map((char: string) => convertKey(keyboardLayout, char))
              .join('')

            if (keyboardLayout === 'qwertz') {
              this.keyOn('shift')
                .keyTap('backquote')
                .keyOff('shift')
                .typeString(string)
                .keyTap('enter')
                .keyOn('shift')
                .keyTap('backquote')
                .keyOff('shift')
            } else {
              this.keyTap('backquote')
                .typeString(string)
                .keyTap('enter')
                .keyTap('backquote')
            }
            break
          }
          case 'playMP3':
            this.playMP3(mfdArray[i].sound)
            break
          case 'hostKeybinds':
            console.log('Command: ', mfdArray[i].game, mfdArray[i].section, mfdArray[i].command)
            this.hostKeybinds(mfdArray[i].game, mfdArray[i].section, mfdArray[i].command)
            break
          default:
            break
          }
        }
      } else {
        console.warn('Button action is not active')
      }
    }

    queueAction = (action: any) => {
      if (typeof action === 'object') {
        if (!('type' in action)) {
          return console.error('An action must contain a "type" field. Received: ', action)
        }

        if (!(ACTION_TYPES as any)[action.type]) {
          return console.error(`An action must a valid type field. Received: ${action.type}`, 'Valid types: ', Object.keys(ACTION_TYPES))
        }

        if (!Object.keys(action).every(key => allowedFields.includes(key))) {
          return console.error(`An action can only contain these fields: ${allowedFields.join(', ')}. Received: `, action)
        }

        this.queue.push(() => {
          this.webrtc.send(action)
          return Promise.resolve()
        })
      } else {
        return console.error('An action must be an object.')
      }
    }

    gameSpecificAction = ({ game, action }: any) => {
      if (game && action) {
        this.queueAction({
          type: ACTION_TYPES.gameSpecificAction,
          payload: { game, action }
        })
      }
    }

    prefs = (prefs = false as any) => {
      return new Promise(resolve => {
        if (prefs && this.hasEverReceivedPrefs) {
          const settings = validateSettings({
            ...getDefaultsAndStorageSettings(),
            ...this.settings,
            ...prefs
          }, this.user)

          this.webrtc.sendWhenConnected({
            type: ACTION_TYPES.setPrefs,
            payload: { prefs: settings }
          })
        } else {
          this.webrtc.sendWhenConnected({ type: ACTION_TYPES.getPrefs })
        }

        const timeout = setTimeout(() => {
          const settings = {
            ...getDefaultsAndStorageSettings(),
            ...this.settings
          }

          resolve(settings)
          if (!this.hasEverReceivedPrefs) {
            this.setConnected(ConnectedStates.DISCONNECTED)
          }
        }, CONNECTED_TIMEOUT)

        this.resolvers.prefs.push((prefs: any) => {
          clearTimeout(timeout)
          resolve(prefs)
          this.setConnected(ConnectedStates.CONNECTED)
        })
      })
    }

    hostKeybinds = (game: any, section: any, command: any) => {
      const payload = { program: game, shard: section, command }
      this.queueAction({ type: 'run', payload })
      return this
    }

    buildKeyHandler = (type: string) => {
      return (key: any, modifiers?: any, sound?: any) => {
        if (typeof key !== 'string') {
          console.error(`Bad input, use mfd.${type}(key)`)
        } else {
          const payload = { key, modifiers, sound }
          this.queueAction({ type, payload })
        }
        return this
      }
    }

    keyUp: any
    keyOff: any
    keyDown: any
    keyOn: any
    keyTap: any
    keyPress: any
    mouseClick: any
    mouseOn: any
    mouseOff: any
    mouseWheel: any
    wait: any
    openDir: any

    typeString = (string: string) => {

        const payload = {
          string: string.replace(/"/g, '\\"').replace(/\\/g, '\\\\')
        }

        this.queueAction({ type: ACTION_TYPES.typeString, payload })
      return this
    }

    screenshots = {
      get: () => new Promise(resolve => resolve({ files: [] })),
      snap: (payload: any) => new Promise(resolve => {
        this.queueAction({ type: ACTION_TYPES.screenshot, payload })
        resolve(null)
      }),
      remove: () => new Promise(resolve => resolve(null))
    }

    open = (path: string) => {
      this.openDir = this.open
      this.queueAction({ type: ACTION_TYPES.open, payload: { path } })
      return this
    }

    url = (url: string) => {
      this.queueAction({ type: ACTION_TYPES.url, payload: { url } })
      return this
    }

    play: any
    playMP3 = (path: any, volume?: any, useAltSound = true) => {
      this.play = this.playMP3
      if (path) {
        if (typeof path === 'string') {
          this.queueAction({
            type: ACTION_TYPES.play,
            payload: { sound: soundDirectory + path, volume, useAltSound }
          })
        } else {
          console.warn('Bad input, use mfd.play(path)')
        }
      }
      return this
    }

    setClientLocation = (url?: string) => {
      const clientLocation = url || window.location.pathname

      this.webrtc.sendWhenConnected({
        type: ACTION_TYPES.setClientLocation,
        payload: { clientLocation }
      })
    }

    sync = ({ update = {} } = {}) => this.update(update)

    update = async (update: any = {}) => {
      this.mfdUpdate = this.update
      const settings = {
        ...(await this.prefs() as Record<string, any>),
        ...update
      }

      this.settings = await this.prefs(settings)

      return this
    }

    mfdUpdate: any
  }
})()

// Wire up key handlers after class definition
;(MFD.prototype as any).keyUp = (MFD.prototype as any).keyOff = function(this: any) {
  // eslint-disable-next-line prefer-rest-params
  return this.buildKeyHandler(ACTION_TYPES.keyUp)(...arguments)
}
;(MFD.prototype as any).keyDown = (MFD.prototype as any).keyOn = function(this: any) {
  // eslint-disable-next-line prefer-rest-params
  return this.buildKeyHandler(ACTION_TYPES.keyDown)(...arguments)
}
;(MFD.prototype as any).keyTap = (MFD.prototype as any).keyPress = function(this: any) {
  // eslint-disable-next-line prefer-rest-params
  return this.buildKeyHandler(ACTION_TYPES.keyTap)(...arguments)
}

const connectToHostLoadingMessage = (seconds = 0) => `Connecting to host... (${seconds}s)`

interface MFDProviderProps {
  children?: ReactNode
}

const MFDProvider = (props: MFDProviderProps) => {
  const [mfd, setMfd] = useState((MFD as any).current.mfd)
  const [connected, setConnected] = useState<any>(null)
  const [isReloading, setReloading] = useState(false)
  const [settings, setSettings] = useState((MFD as any).current.settings)
  const [visibilityTime, setVisibilityTime] = useState(0)
  const { addAnnouncement, removeAnnouncement } = useAnnouncementsContext()
  const { user } = useUser()
  const [cancelClicked, setCancelClicked] = useState(false)
  const router = useRouter()
  const location = useLocation()
  const navigate = useNavigate()
  const intervalRef = useRef<any>(undefined)
  const [loadingMessage, setLoadingMessage] = useState('')

  const reload = () => {
    removeAnnouncement(ErrorEnum.HOST_CONNECTION_ERROR)
    setLoadingMessage('Reconnecting...')
    setReloading(true)
    window.location.reload()
  }

  useEffect(() => {
    if(!isApp()) {
      user ? void noSleep.enable() : void noSleep.disable()
    }
  }, [user])

  useEffect(() => {
    let prevConnected = webrtc?.peer?.connected
    let prevHiddenTime = Date.now()
    const reloadTime = isIOS ? 30 * 1000 : 10 * 60 * 1000
    const onVisibilityChange = () => {
      const diffTime = Date.now() - prevHiddenTime
      if (window.document.hidden) {
        prevHiddenTime = Date.now()
        prevConnected = webrtc?.peer?.connected
      } else if ((diffTime >= reloadTime || !webrtc?.peer?.connected) && prevConnected) {
        reload()
      } else if (prevConnected) {
        setVisibilityTime(Date.now())
      }
    }

    if (isMobile) {
      window.document.addEventListener('visibilitychange', onVisibilityChange)
      return () => window.document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [])

  useEffect(() => {
    if (user) {
      if (TEST_EMAILS.includes((user.primaryEmailAddress as any).emailAddress)) {
        setConnected(true)
        setMfd(true)
        setSettings({})
      } else {
        const newMFD = new (MFD as any)({
          user,
          connectedCallback: (connected: any) => {
            setConnected(connected)
          },
          settingsChangedCallback: (settings: any) => setSettings({ ...settings })
        })
        newMFD.update().then((updatedMFD: any) => {
          setMfd(updatedMFD)
          setConnected(updatedMFD.connected)
          setSettings({ ...updatedMFD.settings })
        })
        return () => {
          setMfd(null)
          setConnected(ConnectedStates.CONNECTING)
          setSettings(null)
        }
      }
    }
  }, [user])

  useEffect(() => {
    const diff = Date.now() - visibilityTime
    if (connected !== ConnectedStates.CONNECTED && diff <= 5000) {
      reload()
    } else if (connected === ConnectedStates.DISCONNECTED) {
      addAnnouncement(ErrorEnum.HOST_CONNECTION_ERROR)
    } else if (connected === ConnectedStates.CONNECTED) {
      removeAnnouncement(ErrorEnum.HOST_CONNECTION_ERROR)
    }
  }, [connected, addAnnouncement])

  useEffect(() => {
    if (!router) {
      return
    }
    const setClientLocation = (url?: string) => {
      mfd?.setClientLocation?.(url)
    }

    setClientLocation()

    return router.subscribe('onResolved', setClientLocation as any)
  }, [router, mfd])

  useEffect(() => {
    if (connected !== ConnectedStates.CONNECTING) {
      clearInterval(intervalRef.current)
      intervalRef.current = undefined
      setLoadingMessage('')
    } else if (!intervalRef.current) {
      const start = Date.now()
      setLoadingMessage(connectToHostLoadingMessage())
      intervalRef.current = setInterval(() => {
        setLoadingMessage(connectToHostLoadingMessage(Math.round((Date.now() - start) / 1000)))
      }, 1000)
    }

    return () => {
      clearInterval(intervalRef.current)
      intervalRef.current = undefined
    }
  }, [connected])

  const onCancelClick = useCallback(() => {
    const url = location.pathname.split('/').slice(0, -1).join('/')
    storage.setItem(LAST_LOCATION, url)
    setCancelClicked(true)
    navigate({ to: url, replace: true })
  }, [setCancelClicked, location, navigate])

  const showCancel = useMemo(() =>
    /\/shardlibrary\/\d+\/\d+.*/.test(storage.getItem(LAST_LOCATION) as string || ''), [])

  const shardName = useMemo(() => storage.getItem(LAST_SHARD_NAME), [])

  const loadingComponent = useMemo(() =>
    showCancel ? <div className="flex flex-col items-center justify-center gap-4">
      <div className="text-center">{loadingMessage}</div>
      <div
        className="flex flex-row flex-grow gap-4 !font-normal !text-sm items-center">{cancelClicked ? <>Redirecting
        to library</> : <>Redirecting to {shardName ? `"${shardName}"` : 'last used shard'} <Button
          style={{ width: 'auto', backgroundColor: '#b91c1c', padding: '4px 8px' }}
          className="!whitespace-nowrap !m-0 !normal-case !text-nowrap !font-normal !text-sm !text-center !inline-block"
          onClick={onCancelClick}>Cancel</Button></>}</div>
    </div> : loadingMessage, [loadingMessage, showCancel, onCancelClick, shardName, cancelClicked])

  return (
    <MfdContext.Provider value={{
      mfd,
      connected,
      settings,
      mfdConnected: connected,
      mfdSettings: settings
    }}>
      {(mfd && settings && (connected === ConnectedStates.CONNECTED) && !isReloading) ? props.children :
        <Loading style={{ zIndex: 10000 }} message={loadingComponent as string} />}
    </MfdContext.Provider>
  )
}

export default MFDProvider
